import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './AdminFeatureTogglePage.css';

const AdminFeatureTogglePage = () => {
  const navigate = useNavigate();
  const [activeTier, setActiveTier] = useState('public');
  const [toggles, setToggles] = useState(null); // null until confirmed loaded from server
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const features = [
    { key: 'education', label: 'Education Programs' },
    { key: 'health', label: 'Health Programs' },
    { key: 'culture', label: 'Culture Programs' },
    { key: 'legal', label: 'Legal Services' },
    { key: 'harvesting', label: 'Harvesting Information' },
    { key: 'communityResources', label: 'Community Resources' },
    { key: 'consultation', label: 'Consultation Projects' },
    { key: 'communityOutreach', label: 'Community Outreach' },
    { key: 'council', label: 'Council' },
    { key: 'hr', label: 'Human Resources' },
    { key: 'faq', label: 'FAQ' }
  ];

  // Load toggles from API on mount
  useEffect(() => {
    const fetchToggles = async () => {
      try {
        const response = await api.get('/api/feature-toggles');
        console.log('Fetched toggles:', response.data);
        
        // Require all three tiers to be present before marking as loaded
        if (response.data.public && response.data.user && response.data.citizen) {
          setToggles({
            public: response.data.public,
            user: response.data.user,
            citizen: response.data.citizen
          });
          setLoadError(false);
        } else {
          console.error('Toggle response missing tier data - refusing to overwrite DB with partial data');
          setLoadError(true);
        }
      } catch (error) {
        console.error('Error fetching feature toggles:', error);
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchToggles();
  }, []);

  const handleToggleChange = (tier, feature) => {
    setToggles({
      ...toggles,
      [tier]: {
        ...toggles[tier],
        [feature]: !toggles[tier][feature]
      }
    });
  };

  const handleSave = async () => {
    if (!toggles || loadError) {
      alert('Cannot save — toggle data was not loaded from server. Please refresh the page.');
      return;
    }
    try {
      setSaving(true);
      await api.put('/api/feature-toggles', toggles);
      alert('Feature toggles updated successfully');
      navigate('/admin-dashboard');
    } catch (error) {
      console.error('Error saving feature toggles:', error);
      alert('Failed to save feature toggles');
    } finally {
      setSaving(false);
    }
  };

  const copyToLowerTiers = (tier) => {
    if (tier === 'citizen') {
      setToggles({
        ...toggles,
        user: { ...toggles.citizen },
        public: { ...toggles.citizen }
      });
    } else if (tier === 'user') {
      setToggles({
        ...toggles,
        public: { ...toggles.user }
      });
    }
  };

  return (
    <div className="admin-feature-toggle-container">
      <div className="page-header">
        <h1>🎛️ Multi-Tier Feature Toggle Settings</h1>
        <p className="subtitle">Control feature visibility for different user access levels</p>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : loadError ? (
        <div style={{padding:'2rem',textAlign:'center'}}>
          <p style={{color:'red',fontSize:'1.1rem',fontWeight:'bold'}}>⚠️ Failed to load toggle settings from server.</p>
          <p>Saving is disabled to protect existing settings. Refresh to try again.</p>
          <button onClick={() => window.location.reload()} className="btn-save" style={{marginTop:'1rem'}}>🔄 Reload Page</button>
        </div>
      ) : (
        <>
          {/* Tier Selection Tabs */}
          <div className="tier-tabs">
            <button 
              className={`tier-tab ${activeTier === 'public' ? 'active' : ''}`}
              onClick={() => setActiveTier('public')}
            >
              🌍 Public
              <span className="tier-description">Not logged in</span>
            </button>
            <button 
              className={`tier-tab ${activeTier === 'user' ? 'active' : ''}`}
              onClick={() => setActiveTier('user')}
            >
              👤 User
              <span className="tier-description">Logged in</span>
            </button>
            <button 
              className={`tier-tab ${activeTier === 'citizen' ? 'active' : ''}`}
              onClick={() => setActiveTier('citizen')}
            >
              ✓ Citizen
              <span className="tier-description">Verified with card</span>
            </button>
          </div>

          {/* Tier Info */}
          <div className="tier-info">
            {activeTier === 'public' && (
              <div className="info-box public">
                <strong>Public Access:</strong> Features visible to visitors who are not logged in. 
                Recommended: Basic information pages only.
              </div>
            )}
            {activeTier === 'user' && (
              <div className="info-box user">
                <strong>User Access:</strong> Features visible to logged-in users who haven't verified their citizen status. 
                Recommended: Most features enabled.
              </div>
            )}
            {activeTier === 'citizen' && (
              <div className="info-box citizen">
                <strong>Citizen Access:</strong> Features visible to verified citizens with a valid card number. 
                Recommended: All features enabled including exclusive programs.
              </div>
            )}
          </div>

          {/* Feature Toggles */}
          <div className="features-grid">
            {features.map(feature => (
              <div key={feature.key} className="feature-toggle-item">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={toggles[activeTier][feature.key]}
                    onChange={() => handleToggleChange(activeTier, feature.key)}
                  />
                  <span className="toggle-switch"></span>
                  <span className="feature-name">{feature.label}</span>
                </label>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="actions-bar">
            <button 
              type="button" 
              className="btn-copy"
              onClick={() => copyToLowerTiers(activeTier)}
              disabled={activeTier === 'public' || !toggles}
            >
              📋 Copy to Lower Tiers
            </button>
            
            <div className="action-buttons">
              <button 
                type="button" 
                className="btn-cancel"
                onClick={() => navigate('/admin-dashboard')}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-save"
                onClick={handleSave} 
                disabled={saving || !toggles || loadError}
              >
                {saving ? '💾 Saving...' : '💾 Save All Tiers'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminFeatureTogglePage;
